import React from 'react'
import { ControlledModal } from '../../layouts/components/modal'
import { Guardian, Student, Teacher, useAppContext, User } from '../../providers/context'
import { makeRequest, showAxiosErrorAsToast } from '../../request'
import { Button } from '../../components/simple/button'
import { LabeledCheckbox } from '../../components/form/checkbox'
import { BlueInput, InputRightFlag, TextArea, TextInput } from '../../components/form/textInput'
import { Divider, Spacer } from '../../components/simple/divider'
import { useTranslation } from '../../providers/translation'
import { IconGlobe, IconLanguage, IconStar } from '../../icons/library'
import { Select } from '../../components/form/select'
import { Toasts } from '../../providers/toast'
import assert from 'assert'
import { IconNoTextToSpeech, IconTranslatedCallSlash, IconTranslatedCall } from '../../icons/translatedCall'
import { TEACHER_TYPES } from './teacherType'
import { Popups } from '../../providers/popups'
import { useLanguages } from '../../providers/languages'

type EditError = 'invalid-phone' | 'change-phone-to-blocked' | 'change-email-to-blocked' | 'invalid-email'
export type EditResponse = 'success' | EditError[]

export const EditUserModal: React.FC<{ user: User; onClose: () => void }> = ({ user, onClose }) => {
  return user.type === 'teacher' ? (
    <TeacherEditModal user={user} onClose={onClose} />
  ) : (
    <StudentGuardianEditModal user={user} onClose={onClose} />
  )
}

const TeacherEditModal: React.FC<{ user: Teacher; onClose: () => void }> = ({ user, onClose }) => {
  const { reloadUser } = useAppContext()
  const [loading, setLoading] = React.useState(false)
  const [phoneNumber, setPhoneNumber] = React.useState(user.formattedPhoneNumber || '')
  const [phoneInvalid, setPhoneInvalid] = React.useState(false)
  const [preferredName, setPreferredName] = React.useState(user.preferredName || '')
  const [forwardMessagesToEmail, setForwardMessagesToEmail] = React.useState(user.forwardMessagesToEmail)
  const [receiveCalls, setReceiveCalls] = React.useState(user.receiveCalls)
  const [forwardSharedMessagesToEmail, setForwardSharedMessagesToEmail] = React.useState(
    user.forwardSharedMessagesToEmail
  )
  const [showAllClasses, setShowAllClasses] = React.useState(user.showAllClasses)
  const [outOfOffice, setOutOfOffice] = React.useState(user.outOfOffice === null ? undefined : user.outOfOffice)
  const [teacherType, setTeacherType] = React.useState(
    user.teacherType === undefined
      ? TEACHER_TYPES[0]
      : TEACHER_TYPES.find((t) => t.value === user.teacherType) || TEACHER_TYPES.find((t) => t.value === 'Other')!
  )
  const [teacherTypeOtherMessage, setTeacherTypeOtherMessage] = React.useState(user.teacherTypeOtherMessage || '')

  return (
    <ControlledModal
      size="sm"
      onOpenChange={(o) => {
        if (!o) onClose()
      }}
      open={true}
      title={'Edit User Preferences'}
      disabled={loading}
    >
      <div className="flex flex-col gap-1">
        Email Address
        <TextInput disabled value={user.email} blue />
        {user.district.showForwardingOptions && (
          <>
            <LabeledCheckbox
              checked={forwardMessagesToEmail}
              onChecked={setForwardMessagesToEmail}
              label="Forward received messages to your email"
            />
            {forwardMessagesToEmail && (
              <LabeledCheckbox
                checked={forwardSharedMessagesToEmail}
                onChecked={setForwardSharedMessagesToEmail}
                label="Forward messages you are shared on to your email"
              />
            )}
            <Spacer />
          </>
        )}
      </div>
      <div className="flex flex-col gap-1">
        Visible Email Address
        <TextInput disabled value={user.visibleEmail} blue />
      </div>

      {user.district.phoneEnabled && (
        <>
          <div className="flex flex-col gap-1">
            Phone Number
            <TextInput
              value={phoneNumber}
              onChange={setPhoneNumber}
              placeholder="xxx-xxx-xxxx"
              disabled={!user.canEditOwnPhoneNumber}
              blue
              invalid={phoneInvalid}
              validation="phone"
            />
          </div>
          <div className="-mt-1 text-sm">
            This number is not shared with families and is only used for crucial messages from the district.
          </div>

          {user.district.massCallingEnabled && (
            <LabeledCheckbox
              checked={receiveCalls}
              disabled={!user.canEditOwnPhoneNumber}
              onChecked={async (c) => {
                if (
                  c ||
                  (await Popups.simpleConfirm(
                    'Are you sure you want to opt-out of district phone call broadcasts? You may miss out on crucial alerts.'
                  ))
                )
                  setReceiveCalls(c)
              }}
              label={'Send me broadcast calls'}
            />
          )}
        </>
      )}
      <div className="mb-3 flex flex-col gap-1">
        Preferred Name
        <TextInput value={preferredName} onChange={setPreferredName} blue />
      </div>

      {user.admin > 0 && user.schools.length > 0 && (
        <LabeledCheckbox
          checked={showAllClasses}
          onChecked={setShowAllClasses}
          label="Show all classes for your school in the recipient selector"
        />
      )}

      <div className="flex items-center gap-2">
        Role:
        <Select small blue options={TEACHER_TYPES} value={teacherType} onChange={setTeacherType} />
      </div>
      {teacherType.value === 'Other' && (
        <>
          If other, please specify
          <TextInput blue value={teacherTypeOtherMessage} onChange={setTeacherTypeOtherMessage} placeholder="role" />
          <Divider />
        </>
      )}

      <LabeledCheckbox
        checked={outOfOffice !== undefined}
        onChecked={() => {
          setOutOfOffice(outOfOffice !== undefined ? undefined : '')
        }}
        label="Enable Out of Office"
        className="mt-2"
      />

      {outOfOffice !== undefined && (
        <TextArea
          className={BlueInput}
          rows={3}
          value={outOfOffice}
          onChange={setOutOfOffice}
          placeholder={user.defaultOutOfOfficeMessage}
        />
      )}

      <Button
        logo
        disabled={loading}
        className="mt-2 self-start"
        onClick={async () => {
          if (
            !phoneNumber.trim() &&
            user.formattedPhoneNumber?.trim() &&
            !(await Popups.simpleConfirm(
              'Are you sure you want to remove your phone number. This number is not shared with families and is only used for crucial messages from the district.'
            ))
          ) {
            setPhoneNumber(user.formattedPhoneNumber || '')
            return
          }
          setPhoneInvalid(false)
          setLoading(true)
          makeRequest<EditResponse>('/frontend_api/editUser', {
            phoneNumber: user.district.phoneEnabled && user.canEditOwnPhoneNumber ? phoneNumber : undefined,
            preferredName: preferredName,
            forwardMessagesToEmail: user.district.showForwardingOptions ? forwardMessagesToEmail : undefined,
            forwardSharedMessagesToEmail: user.district.showForwardingOptions
              ? forwardSharedMessagesToEmail && forwardMessagesToEmail
              : undefined,
            showAllClasses: user.admin > 0 && user.schools.length > 0 ? showAllClasses : undefined,
            receiveCalls: user.district.massCallingEnabled ? receiveCalls : undefined,
            outOfOffice: outOfOffice !== undefined,
            outOfOfficeMessage: outOfOffice,
            teacherType: teacherType.value,
            teacherTypeOtherMessage,
          })
            .then((r) => {
              if (r === 'success') {
                reloadUser()
                onClose()
              } else showEditErrors(r, setPhoneInvalid)
            })
            .catch(showAxiosErrorAsToast)
            .finally(() => setLoading(false))
        }}
      >
        Save
      </Button>
    </ControlledModal>
  )
}

const StudentGuardianEditModal: React.FC<{ user: Student | Guardian; onClose: () => void }> = ({ user, onClose }) => {
  const { reloadUser } = useAppContext()
  const [loading, setLoading] = React.useState(false)
  const { tr } = useTranslation()
  const { changeLanguage } = useTranslation()

  const userEmailInvalid = user.email.substring(0, 1) === '-'
  const cleanUserEmail = userEmailInvalid ? user.email.substring(1) : user.email
  const [email, setEmail] = React.useState(cleanUserEmail)
  const [emailInvalid, setEmailInvalid] = React.useState(false)
  const [receiveEmails, setReceiveEmails] = React.useState(user.receiveEmails)

  const [phoneNumber, setPhoneNumber] = React.useState(user.formattedPhoneNumber)
  const [phoneInvalid, setPhoneInvalid] = React.useState(false)
  const [receiveCalls, setReceiveCalls] = React.useState(user.receiveCalls)

  const [receiveTexts, setReceiveTexts] = React.useState(user.receiveTexts)
  const [receiveTextsAsWhatsapp, setReceiveTextsAsWhatsapp] = React.useState(user.receiveTextsAsWhatsapp)

  const [language, setLanguage] = React.useState(user.language)
  const [spokenLanguage, setSpokenLanguage] = React.useState(user.spokenLanguage)

  const [preferredName, setPreferredName] = React.useState(user.preferredName)

  const [sendAttendanceThroughEmail, setSendAttendanceThroughEmail] = React.useState(
    user.type === 'guardian' && user.sendAttendanceThroughEmail
  )
  const [sendAttendanceThroughText, setSendAttendanceThroughText] = React.useState(
    user.type === 'guardian' && user.sendAttendanceThroughText
  )

  const showAttendanceOptions = user.type === 'guardian' && user.children.some((c) => c.getsAttendanceReports)
  const showPhoneOptions = user.district.phoneEnabled && (user.type === 'guardian' || user.oldEnoughForPhone)
  const showTextOptions = showPhoneOptions && !user.hasLandline
  const showEmailOptions = user.type === 'guardian' && !user.faria

  return (
    <ControlledModal
      size="sm"
      onOpenChange={(o) => {
        if (!o) onClose()
      }}
      open={true}
      title={tr('Edit User Preferences')}
      disabled={loading}
    >
      {!user.allowSelfSISEdits && (
        <div className="max-w-[340px] rounded bg-warning p-2 text-sm">
          {tr('You cannot edit your email, phone or lanugage info here, please reach out to your school.')}
        </div>
      )}
      {showEmailOptions ? (
        <div className="flex flex-col gap-1">
          {tr('Email Address')}
          <TextInput
            value={email}
            onChange={setEmail}
            placeholder="email@example.com"
            invalid={userEmailInvalid || emailInvalid}
            disabled={!user.allowSelfSISEdits}
            blue
            rightIcons={
              user.pendingEmailChange && (
                <IconStar
                  className="text-warning"
                  size="lg"
                  tooltip={tr('This email has an unconfirmed change to') + ' ' + user.pendingEmailChange}
                />
              )
            }
            validation="email"
          />
          {email !== cleanUserEmail && (
            <div className="max-w-[340px] text-sm opacity-70">
              {tr('An email change will not occur until you confirm it through an email we send to the new address.')}
            </div>
          )}
          <LabeledCheckbox checked={receiveEmails} onChecked={setReceiveEmails} label={tr('Send me emails')} />
        </div>
      ) : (
        <div className="flex flex-col gap-1">
          {tr('Email Address')}
          <TextInput disabled value={email} invalid={userEmailInvalid} blue />
        </div>
      )}

      <div className="mt-1" />

      {showPhoneOptions && (
        <div className="mb-3 flex flex-col gap-1">
          {tr('Phone Number')}
          <TextInput
            value={phoneNumber || ''}
            onChange={setPhoneNumber}
            invalid={phoneInvalid}
            disabled={!user.allowSelfSISEdits}
            placeholder="xxx-xxx-xxxx"
            blue
            rightIcons={
              user.pendingPhoneChange && (
                <IconStar
                  className="text-warning"
                  size="lg"
                  tooltip={tr('This phone number has an unconfirmed change to') + ' ' + user.pendingPhoneChange}
                />
              )
            }
            validation="phone"
          />
          {user.hasLandline && (
            <div className="text-sm text-red-500">this number is a landline, and cannot receive texts</div>
          )}
          {phoneNumber !== user.formattedPhoneNumber && (
            <div className="max-w-[340px] text-sm opacity-70">
              {tr(
                'A phone number change will not occur until you confirm it by clicking the link we send to the new number.'
              )}
            </div>
          )}
          {showTextOptions && (
            <>
              {!receiveTextsAsWhatsapp && (
                <>
                  <LabeledCheckbox
                    disabled={user.blockedTextNumbers.length > 0}
                    checked={receiveTexts}
                    onChecked={setReceiveTexts}
                    label={tr('Send me texts')}
                  />
                  {user.blockedTextNumbers.length > 0 && (
                    <div className="text-sm">
                      {tr('Text START to')} {user.blockedTextNumbers.join(', ')} {tr('to re-enable texting')}
                    </div>
                  )}
                </>
              )}
              {receiveTexts && (
                <LabeledCheckbox
                  checked={receiveTextsAsWhatsapp}
                  onChecked={setReceiveTextsAsWhatsapp}
                  label={tr('Send text messages through WhatsApp')}
                />
              )}
            </>
          )}
          {user.district.massCallingEnabled && (
            <LabeledCheckbox
              checked={receiveCalls}
              onChecked={async (c) => {
                if (
                  c ||
                  (await Popups.simpleConfirm(
                    'Are you sure you want to opt-out of district phone call broadcasts? You may miss out on crucial alerts.'
                  ))
                )
                  setReceiveCalls(c)
              }}
              label={tr('Send me broadcast calls')}
            />
          )}
        </div>
      )}

      <div className="flex flex-col gap-1">
        {tr('Language')}
        <LanguageEdit
          language={language}
          setLanguage={setLanguage}
          spokenLanguage={spokenLanguage}
          setSpokenLanguage={setSpokenLanguage}
          canEditLanguage={user.allowSelfSISEdits}
          showNativeNames
        />
      </div>

      <Spacer />
      <div className="flex flex-col gap-1">
        {tr('Preferred Name')}
        <TextInput value={preferredName || ''} onChange={setPreferredName} blue disabled={!user.allowSelfSISEdits} />
      </div>

      {showAttendanceOptions && (
        <>
          <Spacer />
          <LabeledCheckbox
            checked={sendAttendanceThroughEmail}
            onChecked={setSendAttendanceThroughEmail}
            label={tr('Send attendance reports through email')}
          />
          <LabeledCheckbox
            checked={sendAttendanceThroughText}
            onChecked={setSendAttendanceThroughText}
            label={tr('Send attendance reports through text')}
          />
        </>
      )}

      <Button
        logo
        disabled={loading}
        className="mt-2 self-start"
        onClick={() => {
          setPhoneInvalid(false)
          setEmailInvalid(false)
          setLoading(true)
          makeRequest<EditResponse>('/frontend_api/editUser', {
            email: showEmailOptions && user.allowSelfSISEdits && email !== cleanUserEmail ? email : undefined,
            receiveEmails: showEmailOptions ? receiveEmails : undefined,
            phoneNumber: showPhoneOptions && user.allowSelfSISEdits ? phoneNumber : undefined,
            receiveTexts: showPhoneOptions && showTextOptions ? receiveTexts : undefined,
            receiveTextsAsWhatsapp:
              showPhoneOptions && showTextOptions && receiveTexts ? receiveTextsAsWhatsapp : undefined,
            language: user.allowSelfSISEdits ? language : undefined,
            spokenLanguage: spokenLanguage || '',
            preferredName: user.allowSelfSISEdits && !!preferredName ? preferredName : undefined,
            sendAttendanceThroughEmail: showAttendanceOptions ? sendAttendanceThroughEmail : undefined,
            sendAttendanceThroughText: showAttendanceOptions ? sendAttendanceThroughText : undefined,
            receiveCalls: showPhoneOptions && user.district.massCallingEnabled ? receiveCalls : undefined,
          })
            .then((r) => {
              if (r === 'success') {
                if (language !== user.language) changeLanguage(language)
                if (user.type === 'guardian' && (email !== cleanUserEmail || phoneNumber !== user.formattedPhoneNumber))
                  Popups.confirm({
                    title: tr('Thank you for updating your contact information!'),
                    message: tr(
                      'Please notify the school about any changes to ensure you receive all important communications.'
                    ),
                    yesText: tr('OK'),
                  })
                reloadUser()
                onClose()
              } else showEditErrors(r, setPhoneInvalid, setEmailInvalid, tr)
            })
            .catch(showAxiosErrorAsToast)
            .finally(() => setLoading(false))
        }}
      >
        Save
      </Button>
    </ControlledModal>
  )
}

export const showEditErrors = (
  errors: EditError[],
  setPhoneInvalid?: (v: boolean) => void,
  setEmailInvalid?: (v: boolean) => void,
  tr: (s: string) => string = (s) => s
) => {
  errors.forEach((e) => {
    switch (e) {
      case 'invalid-phone':
        if (setPhoneInvalid) setPhoneInvalid(true)
        else Toasts.showToast({ message: tr('Invalid phone number'), error: true })
        break
      case 'invalid-email':
        if (setEmailInvalid) setEmailInvalid(true)
        else Toasts.showToast({ message: tr('Invalid email address'), error: true })
        break
      case 'change-phone-to-blocked':
        if (setPhoneInvalid) setPhoneInvalid(true)
        Toasts.showToast({
          message: tr(
            'The number you requested has asked not to be messaged by us, please re-enable texting to that number and try again.'
          ),
          error: true,
          duration: 10000,
        })
        break
      case 'change-email-to-blocked':
        if (setEmailInvalid) setEmailInvalid(true)
        Toasts.showToast({
          message: tr(
            'The email you requested has asked not to be messaged by us, please re-enable sending to that email and try again.'
          ),
          error: true,
          duration: 10000,
        })
        break
    }
  })
}

export const LanguageEdit: React.FC<{
  language: string
  setLanguage: React.Dispatch<React.SetStateAction<string>>
  spokenLanguage: string | undefined
  setSpokenLanguage: React.Dispatch<React.SetStateAction<string | undefined>>
  canEditLanguage?: boolean
  showNativeNames?: boolean
}> = ({ language, setLanguage, spokenLanguage, setSpokenLanguage, canEditLanguage = true, showNativeNames }) => {
  const { tr } = useTranslation()
  const languages = useLanguages()
  const languageOptions = React.useMemo(() => languages.getOptions((l) => l.isStandard, showNativeNames), [languages])
  const spokenLanguageOptions = React.useMemo(() => languages.getOptions(), [languages])
  const selectedLanguage = languageOptions.find((l) => l.value === language)
  assert(selectedLanguage, 'Language not found')
  const selectedSpokenLanguage = spokenLanguageOptions.find((l) => l.value === spokenLanguage)

  const languageData = languages.find(language)
  const spokenLanguageData = languages.find(spokenLanguage)

  const noTTS = !languageData?.canTextToSpeech
  const noLK = !languageData?.lexikeetCode && language !== 'en'
  const noSpokenTTS = spokenLanguageData && !spokenLanguageData.canTextToSpeech
  const noSpokenLK = spokenLanguageData && !spokenLanguageData.lexikeetCode && spokenLanguage !== 'en'
  const noSpokenTranslation = spokenLanguageData && !spokenLanguageData.googleTranslatable && spokenLanguage !== 'en'

  return (
    <div className="flex flex-grow flex-col gap-2">
      <Select
        options={languageOptions}
        value={selectedLanguage}
        onChange={(option) => setLanguage(option.value)}
        blue
        icon={<IconGlobe />}
        disabled={!canEditLanguage}
        rightIcons={
          (noTTS || noLK) && (
            <InputRightFlag>
              {noTTS && <IconNoTextToSpeech tooltip="We cannot provide automatic text-to-speech in this language." />}
              {noLK && <IconTranslatedCallSlash tooltip="We cannot provide video interpretation in this language." />}
            </InputRightFlag>
          )
        }
      />
      <LabeledCheckbox
        checked={selectedSpokenLanguage !== undefined}
        onChecked={() => {
          setSpokenLanguage(selectedSpokenLanguage === undefined ? language : undefined)
        }}
        label={tr('Alternative Spoken Language')}
      />
      {selectedSpokenLanguage !== undefined && (
        <Select
          options={spokenLanguageOptions}
          value={selectedSpokenLanguage}
          onChange={(option) => setSpokenLanguage(option.value)}
          blue
          icon={<IconGlobe />}
          rightIcons={
            (noSpokenTTS || noSpokenLK || noSpokenTranslation) && (
              <InputRightFlag>
                {noSpokenTTS && (
                  <IconNoTextToSpeech tooltip="We cannot provide automatic text-to-speech in this language." />
                )}
                {noSpokenLK && (
                  <IconTranslatedCallSlash tooltip="We cannot provide video interpretation in this language." />
                )}
                {noSpokenTranslation && (
                  <>
                    <IconTranslatedCall tooltip="We support video interpretation in this language" />
                    <IconLanguage
                      size="lg"
                      className="text-logo"
                      tooltip="We cannot automatically translate in this language."
                    />
                  </>
                )}
              </InputRightFlag>
            )
          }
        />
      )}
    </div>
  )
}
